// Vendors: third-party organizations Stewardship works with.
// Single table with a type discriminator per CLAUDE.md.

import type { AuditedRow, SoftDeletable, SqliteBoolean, UUID } from './common';
import type { VendorSubtype } from './enums';

export type { VendorSubtype } from './enums';

// Vendor subtype is the person-flavored role recorded on a contact when
// it's reclassified as a vendor in the Cleanup System. The ordered list +
// display labels live here (the type itself is in enums.ts alongside the
// other CHECK-mirrored enums). Used by the cleanup tag-as-vendor picker
// (web) and validated by the decide handler (worker), so both import from
// one place. — Baker 2026-06-27.
export const VENDOR_SUBTYPES: readonly VendorSubtype[] = [
  'real_estate_agent',
  'title_escrow_officer',
  'lender_partner',
  'inspector',
  'carrier_rep',
  'financial_rep',
  'attorney',
  'referral_partner',
  'other',
];

export const VENDOR_SUBTYPE_LABELS: Record<VendorSubtype, string> = {
  real_estate_agent: 'Real Estate Agent',
  title_escrow_officer: 'Title / Escrow Officer',
  lender_partner: 'Lender / Loan Officer Partner',
  inspector: 'Inspector / Appraiser',
  carrier_rep: 'Insurance Carrier Rep',
  financial_rep: 'Custodian / Financial Rep',
  attorney: 'Attorney',
  referral_partner: 'Referral Partner',
  other: 'Other',
};

export function vendorSubtypeLabel(subtype: string | null | undefined): string {
  if (!subtype) return 'Unclassified';
  return VENDOR_SUBTYPE_LABELS[subtype as VendorSubtype] ?? subtype;
}

export type VendorType =
  | 'carrier'
  | 'lender'
  | 'custodian'
  | 'title_company'
  | 'escrow'
  | 'inspector'
  | 'lender_partner'
  | 'referral_source_partner'
  | 'service_provider'
  | 'other';

export interface VendorRow extends AuditedRow, SoftDeletable {
  slug: string | null;
  display_name: string;
  type: VendorType;

  portal_url: string | null;

  primary_contact_name: string | null;
  primary_contact_email: string | null;
  primary_contact_phone: string | null;
  primary_contact_address: string | null;

  relationship_owner_user_id: UUID | null;

  // INTERNAL_ONLY.
  notes: string | null;

  is_active: SqliteBoolean;
}

export interface VendorContactLinkRow extends AuditedRow {
  vendor_id: UUID;
  contact_id: UUID;
  context: string | null;
}
